import { FULL_DATE_FORMAT, REQUIRED_FIELD, SSNMask } from "@/lib/constants";
import {
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Textarea,
  VStack,
  Flex,
} from "@chakra-ui/react";
import { useContext } from "react";
import { InputMask } from "@react-input/mask";
import { Controller } from "react-hook-form";
import LocationSelect from "../LocationSelect";
import GoogleAddressInput from "../dashboard/GoogleAddressInput";
import CurrencyInput from "react-currency-input-field";
import PhoneNumberInput from "./PhoneNumberInput";
import { FormContext } from "@/providers/form";
import RequiredIndicator from "../self_serve/RequiredIndicator";
import { InputWrapper } from "./JailTextInputWrapper";

const custom_fields_width: {
  [key: string]: string;
} = {
  MultiCheckbox: "100%",
};

const ListChildField = ({
  field,
  index,
  control,
  childField,
  childFieldIndex,
  register,
}: {
  field: any;
  index: number;
  control: any;
  childField: any;
  childFieldIndex: number;
  register: any;
}) => {
  const { values, saveForm } = useContext(FormContext);

  const saveValue = (index: number, childField: any, value: any) => {
    saveForm({
      [field.databaseKey]: values[field.databaseKey].map(
        (record: any, i: number) =>
          i === index
            ? {
                ...record,
                [childField.databaseKey]: value,
              }
            : record
      ),
    });
  };

  const childFieldName = `${field.databaseKey}.${index}.${childField.databaseKey}`;

  return (
    <VStack key={childField.databaseKey + childFieldIndex} w="100%">
      <FormControl>
        <FormLabel>
          {childField.label}
          <RequiredIndicator
            value={values[field.databaseKey][index][childField.databaseKey]}
          />
        </FormLabel>
        {childField.description && (
          <FormHelperText mb={2}>{childField.description}</FormHelperText>
        )}
        <Flex width={custom_fields_width[childField.type] || "50%"}>
          {childField.type === "Input" && (
            <InputWrapper
              name={childFieldName}
              control={control}
              rules={{ required: REQUIRED_FIELD }}
              placeholder={childField.placeholder}
              onBlur={(ev: any) => {
                saveValue(
                  index,
                  childField,
                  ev.target.value ||
                    values[field.databaseKey][index][childField.databaseKey]
                );
              }}
            />
          )}
          {childField.type === "Select" && (
            <Select
              placeholder="Choose One"
              {...register(childFieldName, {
                required: REQUIRED_FIELD,
                onBlur: (ev: any) => {
                  saveValue(index, childField, ev.target.value);
                },
              })}
            >
              {(childField.options || []).map((option: string) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          )}
          {childField.type === "YesNoRadioGroup" && (
            /* TODO: REfactor this to use the YesNoRadioGroup component w/ autosave prop */
            <Controller
              name={childFieldName}
              control={control}
              rules={{
                validate: (val) =>
                  val === true || val === false ? true : REQUIRED_FIELD,
              }}
              render={({ field }) => (
                <RadioGroup
                  defaultValue=""
                  w="100%"
                  value={
                    field.value !== undefined && field.value !== null
                      ? field.value.toString()
                      : undefined
                  }
                  onChange={(val) => {
                    const boolVal = val === "true";
                    field.onChange(boolVal);
                    saveValue(index, childField, boolVal);
                  }}
                >
                  <Stack spacing={5} direction="row" display="flex" w="100%">
                    <Radio value="true">Yes</Radio>
                    <Radio value="false">No</Radio>
                  </Stack>
                </RadioGroup>
              )}
            />
          )}
          {childField.type === "FullDate" && (
            <Input
              placeholder={field.placeholder || FULL_DATE_FORMAT}
              as={InputMask}
              mask="__/__/____"
              replacement={{ _: /\d/ }}
              type="text"
              {...register(childFieldName, {
                required: REQUIRED_FIELD,
                onBlur: (ev: any) => {
                  saveValue(index, childField, ev.target.value);
                },
              })}
            />
          )}
          {childField.type === "NumberInput" && (
            <Input
              type="number"
              placeholder="2"
              min="0"
              {...register(childFieldName, {
                required: REQUIRED_FIELD,
                valueAsNumber: true,
                min: 0,
                onBlur: (ev: any) => {
                  saveValue(index, childField, ev.target.value);
                },
              })}
            />
          )}
          {childField.type === "PhoneNumberInput" && (
            <PhoneNumberInput
              width="100%"
              register={register}
              fieldName={childFieldName}
              onBlur={(ev: any) => {
                if (ev.target.value) {
                  saveValue(
                    index,
                    childField,
                    ev.target.value.replace(/\D/g, "")
                  );
                }
              }}
            />
          )}
          {childField.type === "Textarea" && (
            <Textarea
              placeholder={field.placeholder || "Enter more information..."}
              {...register(childFieldName, {
                required: REQUIRED_FIELD,
              })}
            />
          )}
          {childField.type === "DollarInput" && (
            <InputGroup>
              <InputLeftElement pointerEvents="none">$</InputLeftElement>
              <Input
                as={CurrencyInput}
                decimalsLimit={field.decimalsLimit || 2}
                {...register(childFieldName, {
                  required: REQUIRED_FIELD,
                  onBlur: (ev: any) => {
                    saveValue(index, childField, ev.target.value);
                  },
                })}
                placeholder={field.placeholder || "Enter Income"}
              />
            </InputGroup>
          )}

          {childField.type === "MultiCheckbox" && (
            <Controller
              name={childFieldName}
              control={control}
              rules={{ required: REQUIRED_FIELD }}
              render={({ field: { value = [], onChange } }) => {
                const handleCheckboxChange = (checkboxValue: string) => {
                  const updatedValues = value.includes(checkboxValue)
                    ? value.filter((val: string) => val !== checkboxValue)
                    : [...value, checkboxValue];
                  onChange(updatedValues);
                  saveValue(index, childField, updatedValues);
                };

                return (
                  <Stack direction="column" gap="12px">
                    {childField.options.map((option: string) => (
                      <Checkbox
                        key={option}
                        isChecked={value.includes(option)}
                        onChange={() => handleCheckboxChange(option)}
                      >
                        {option}
                      </Checkbox>
                    ))}
                  </Stack>
                );
              }}
            />
          )}

          {childField.type === "AddressAutocomplete" && (
            <Controller
              name={childFieldName}
              control={control}
              rules={{
                required: REQUIRED_FIELD,
              }}
              render={({ field: formField }) => (
                <GoogleAddressInput
                  value={formField.value}
                  onChange={(val) => {
                    formField.onChange(val);
                    saveValue(index, childField, val);
                  }}
                />
              )}
            />
          )}

          {childField.type === "StateSelect" && (
            <Controller
              name={childFieldName}
              control={control}
              rules={{ required: REQUIRED_FIELD }}
              render={({ field: formField }) => (
                <LocationSelect
                  valueKey="name"
                  width="100%"
                  value={formField.value}
                  onChange={(val) => {
                    formField.onChange(val);
                    saveValue(index, childField, val);
                  }}
                />
              )}
            />
          )}

          {childField.type === "SSNInput" && (
            <Input
              type="text"
              placeholder="111-22-3333"
              as={InputMask}
              mask={SSNMask.mask}
              replacement={SSNMask.replacement}
              {...register(childFieldName, {
                required: REQUIRED_FIELD,
                minLength: {
                  value: 9,
                  message: "SSN must be 9 digits",
                },
                maxLength: {
                  value: 9,
                  message: "SSN must be 9 digits",
                },
                setValueAs: (value: string) => value.replace(/\D/g, ""),
                onBlur: (ev: any) => {
                  saveValue(
                    index,
                    childField,
                    ev.target.value.replace(/\D/g, "")
                  );
                },
              })}
            />
          )}
        </Flex>
      </FormControl>
    </VStack>
  );
};

export default ListChildField;
